'use client';

import {
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  type CSSProperties,
  type RefObject,
} from 'react';
import { genieGrowIn, popElement, prefersReducedMotion } from '@/lib/ui/genie';
import { isGlassDocument, slideSheetAway } from '@/lib/ui/glass-exit';

// Slide-down + backdrop fade-out duration on close, kept on one clock so the
// sheet and its dim/blur leave together. Genie sheets also leave this way —
// open grows from the trigger, but close always slides DOWN (see genie.ts).
const EXIT_MS = 300;

// Liquid Glass (variant B): a longer close on the iOS decelerate curve. A's
// ease-IN is fastest at the very end, so the sheet is at full speed as it
// leaves the screen — which on a phone reads as it vanishing (Tom, 2026-10-10).
const GLASS_EXIT_MS = 380;
const GLASS_EXIT_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const exitMs = () => (isGlassDocument() ? GLASS_EXIT_MS : EXIT_MS);
const exitEase = () => (isGlassDocument() ? GLASS_EXIT_EASE : 'cubic-bezier(0.4, 0, 1, 1)');

/**
 * Glass: while the sheet MOVES it drops its blur for a solid fill
 * (`.mucka-mod-sheet[data-moving]` in globals.css). Redrawing a 26px blur
 * under a full-height surface on every frame is what an iPhone drops frames
 * on, and a slide with its middle frames missing looks like a fade.
 */
const setMoving = (sheet: HTMLElement | null, on: boolean) => {
  if (!sheet) return;
  if (on) sheet.dataset.moving = '1';
  else delete sheet.dataset.moving;
};
// useLayoutEffect warns under SSR; fall back to useEffect on the server. Sheet
// is a client component so this resolves to useLayoutEffect in the browser,
// which is what we need to paint the collapsed genie state before first frame.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface UseBottomSheetReturn {
  sheetRef: RefObject<HTMLDivElement | null>;
  backdropRef: RefObject<HTMLDivElement | null>;
  sheetStyle: CSSProperties;
  backdropStyle: CSSProperties;
  handleClose: () => void;
}

export function useBottomSheet(
  onDismiss: () => void,
  // Optional explicit scroll container. The <Sheet> primitive passes its body
  // ref here so drag-vs-scroll detection targets the real scroller. Callers
  // that omit it keep the legacy querySelector fallback below.
  scrollRef?: RefObject<HTMLElement | null>,
  // Optional trigger element. When supplied (and on mobile, where the sheet ref
  // is attached) the sheet opens/closes with a "genie" grow-from / collapse-into
  // this element instead of the plain slide-up. Drag-to-dismiss still slides.
  originRef?: RefObject<HTMLElement | null>,
  /**
   * Asked once, at the moment a swipe-down has earned a dismiss, whether the
   * dismiss may actually happen. Return false and the sheet springs back
   * instead — the caller is expected to be putting something on screen to
   * explain why (an unsaved-work question), or to be mid-save.
   *
   * 🚨 Only the SWIPE consults this, never `dismiss()` / `handleClose()`. Those
   * are the caller's own deliberate close, and are how it dismisses the sheet
   * *after* the question is answered — gating them too would make "close without
   * saving" ask again forever.
   */
  beforeDismiss?: () => boolean,
): UseBottomSheetReturn {
  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Track drag state in refs to avoid re-renders during gestures
  const dragging = useRef(false);
  const startY = useRef(0);
  const currentDelta = useRef(0);
  const positions = useRef<{ y: number; t: number }[]>([]);
  const dismissed = useRef(false);
  const entryDone = useRef(false);
  /**
   * The exit backstop, held so it can be cancelled.
   *
   * 🚨 Both exit paths below race a `transitionend` listener against a timer,
   * and neither used to cancel the loser. Two consequences, and the second is
   * the one that bit:
   *
   *  - In a real browser the transition fires, `onEnd` calls `onDismiss`, and
   *    then the timer calls it AGAIN 40–50ms later, because `dismissed.current`
   *    is still true and nothing had cleared the timeout.
   *  - After unmount the timer survives and calls `onDismiss` on a sheet that
   *    is already gone — up to 450ms later. That is what made
   *    `sheet-close-guard.test.tsx` fail 5 runs in 30: a dismissed sheet from
   *    the previous test fired its backstop during the next one and
   *    incremented that test's shared `closed` counter. It read as a load
   *    flake and was nothing of the kind.
   */
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Whether `onDismiss` has already been called (or must never be again). */
  const finished = useRef(false);

  // Held in a ref so the touch listeners below never re-bind when the caller
  // hands us a fresh closure — rebinding mid-gesture would drop the drag.
  const beforeDismissRef = useRef(beforeDismiss);

  const clearExitTimer = useCallback(() => {
    if (exitTimer.current !== null) {
      clearTimeout(exitTimer.current);
      exitTimer.current = null;
    }
  }, []);

  /**
   * Call `onDismiss` exactly once per sheet, and never after unmount.
   *
   * Every exit path routes through here rather than calling `onDismiss`
   * directly, so whichever of the transition and the backstop gets there first
   * wins and the other becomes a no-op.
   */
  const finishDismiss = useCallback(() => {
    if (finished.current) return;
    finished.current = true;
    clearExitTimer();
    onDismiss();
  }, [onDismiss, clearExitTimer]);

  /**
   * A sheet that unmounts mid-exit must not call back into a caller that has
   * already moved on.
   *
   * 🚨 AND A FRESH MOUNT MUST START UNFINISHED. The reset line is not
   * defensive tidying, it is the whole fix: React StrictMode — on by default in
   * the App Router, so every dev run — invokes effects mount → cleanup → mount.
   * The cleanup therefore fired immediately after the FIRST mount and latched
   * `finished.current = true` for the life of the component, because a ref
   * survives that cycle and nothing ever set it back.
   *
   * From then on `finishDismiss` returned early for ever, so `onDismiss` was
   * never called: the sheet played its exit animation, faded its backdrop to
   * `opacity: 0` and then STAYED MOUNTED, leaving an invisible
   * `pointer-events: auto` layer over the page. The symptom is not "the sheet
   * will not close" — the sheet looks closed. It is "the page underneath stops
   * responding", which is what Tom reported as the list no longer scrolling
   * after opening a message sheet. Esc and the backdrop were equally dead.
   *
   * Mobile only, which is why it survived: on desktop `Sheet` uses `onClose`
   * directly and never reaches this hook.
   */
  useEffect(() => {
    finished.current = false;
    dismissed.current = false;
    return () => {
      finished.current = true;
      clearExitTimer();
    };
  }, [clearExitTimer]);
  useEffect(() => {
    beforeDismissRef.current = beforeDismiss;
  }, [beforeDismiss]);

  // After the CSS entry animation finishes, clear it so we can
  // control transform directly without the animation fighting us.
  // Skipped in genie mode — the genie entry effect below owns entryDone there.
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || originRef?.current) return;
    if (isGlassDocument()) setMoving(sheet, true);

    let settled = false;
    // Unlocks dragging AND hands transform control back to us. Only safe to call
    // once the entry animation is genuinely over — clearing `animation` early
    // would cut the slide-up short.
    const finish = () => {
      if (settled) return;
      settled = true;
      entryDone.current = true;
      setMoving(sheet, false);
      sheet.style.animation = 'none';
      sheet.style.transform = 'translateY(0)';
    };

    // Fast path, and the only path this used to have.
    sheet.addEventListener('animationend', finish, { once: true });

    // ...which is a race, because this is a PASSIVE effect: it runs after the
    // browser has painted, so the CSS entry animation is already under way by the
    // time we get here. If the main thread is busy enough that the animation
    // completes first, animationend is never delivered to us — and since
    // `entryDone` gates every touch handler below, the sheet then stays
    // undraggable for its entire life. Scrolling still works, so it presents as
    // "swipe-to-dismiss just doesn't work on this sheet".
    //
    // That is not hypothetical: it is why the help article sheet was undraggable
    // on device. Its entry animation is 240ms against .mucka-mod-sheet's 420ms,
    // and it opens out of the chat panel mid-message-stream, so it lost the race
    // more or less every time.
    //
    // An animation's own `finished` promise is correct whichever side of the race
    // we land on — an already-completed animation resolves it immediately.
    const anims = typeof sheet.getAnimations === 'function' ? sheet.getAnimations() : [];
    if (anims.length > 0) {
      void Promise.all(anims.map(a => a.finished.catch(() => {}))).then(finish);
    }

    // Last resort, for the cases neither of the above covers: no entry animation
    // at all (a caller whose CSS doesn't animate, or a browser without
    // getAnimations). Comfortably past the longest entry animation in the app, so
    // it can never truncate one — it only ever rescues a sheet that would
    // otherwise be stuck locked.
    const backstop = setTimeout(finish, 600);

    return () => {
      clearTimeout(backstop);
      sheet.removeEventListener('animationend', finish);
    };
  }, [originRef]);

  // Genie entry: grow the sheet out of the trigger element. Runs in a layout
  // effect so the collapsed (scaled-down, transparent) state is painted before
  // the first frame — no flash of the full sheet. On finish the helper clears
  // the inline transform so the drag handlers below operate on a clean
  // translateY base; we just flip entryDone there. Close is a slide-down
  // (handled in dismiss), not a collapse back into the trigger.
  useIsoLayoutEffect(() => {
    const sheet = sheetRef.current;
    const trigger = originRef?.current;
    if (!sheet || !trigger) return; // not genie mode (or desktop — no sheet ref)

    sheet.style.animation = 'none'; // belt-and-braces over the .--genie class
    const glass = isGlassDocument();
    if (glass) setMoving(sheet, true);
    const anim = genieGrowIn(sheet, trigger, {
      // Glass: the sheet is solid almost from the first frame, so it reads as
      // growing out of the button rather than fading in over it.
      fadeUntil: glass ? 0.15 : undefined,
      onFinish: () => { entryDone.current = true; setMoving(sheet, false); },
    });
    popElement(trigger);

    if (!anim) { entryDone.current = true; setMoving(sheet, false); return; }
    return () => { try { anim.cancel(); } catch { /* already gone */ } };
  }, []);

  // Glass: a sheet its caller removes without going through `dismiss` — the
  // common case is a Save that closes it — slides away instead of vanishing.
  // A copy plays the slide; the caller's state has already moved on. Only once
  // the sheet has finished opening, which also keeps StrictMode's mount →
  // cleanup → mount from leaving a ghost behind on every dev open.
  useIsoLayoutEffect(() => {
    const backdrop = backdropRef.current;
    return () => {
      if (dismissed.current || !entryDone.current) return;
      slideSheetAway(backdrop, exitMs(), exitEase());
    };
  }, []);

  // Fade the backdrop's dim + blur out in step with the sheet slide-down. The
  // backdrop fades IN via a CSS animation (`mucka-mod-backdrop-in`, both fill),
  // so we must pin the current opacity, clear that animation and force a reflow
  // before transitioning to 0 — otherwise the animation's `both` fill keeps
  // holding opacity:1 and the fade-out snaps instead of animating.
  const fadeBackdropOut = useCallback((durationMs: number) => {
    const backdrop = backdropRef.current;
    if (!backdrop) return;
    const current = getComputedStyle(backdrop).opacity;
    backdrop.style.animation = 'none';
    backdrop.style.opacity = current;
    void backdrop.offsetWidth; // reflow so the transition has a start value
    // ease-IN: the dim holds while the sheet is still on screen and only drops
    // away as it clears the bottom — so the scene "stays visible all the way
    // off" rather than fading out from under a fast-flicked sheet.
    backdrop.style.transition = `opacity ${durationMs}ms ease-in`;
    backdrop.style.opacity = '0';
  }, []);

  const dismiss = useCallback(() => {
    if (dismissed.current) return;
    dismissed.current = true;

    const reduced = prefersReducedMotion();
    const sheet = sheetRef.current;
    const trigger = originRef?.current;

    // Genie sheets pop the trigger on close too, but still leave DOWNWARD (a
    // scale-collapse into a trigger above the sheet's bottom reads as a floating
    // square). So pop, then fall through to the normal slide-down below. Cancel
    // any still-running genie-open WAA + reset its inline state first so the
    // slide starts from the resting transform (matters if you close mid-open).
    if (trigger) {
      popElement(trigger);
      if (sheet) {
        sheet.getAnimations?.().forEach((a) => { try { a.cancel(); } catch { /* gone */ } });
        sheet.style.opacity = '';
        sheet.style.transform = '';
        sheet.style.transformOrigin = '';
      }
    }

    // Reduced motion (or no sheet): skip the slide, just fade the backdrop.
    if (!sheet || reduced) {
      fadeBackdropOut(reduced ? 160 : EXIT_MS);
      clearExitTimer();
      exitTimer.current = setTimeout(finishDismiss, reduced ? 170 : 0);
      return;
    }

    // Slide the sheet off-screen and fade the backdrop on the same clock.
    const ms = exitMs();
    fadeBackdropOut(ms);
    setMoving(sheet, isGlassDocument());
    sheet.style.transition = `transform ${ms}ms ${exitEase()}`;
    sheet.style.transform = 'translateY(100%)';

    const onEnd = () => {
      sheet.removeEventListener('transitionend', onEnd);
      finishDismiss();
    };
    sheet.addEventListener('transitionend', onEnd);
    // Safety fallback, for a browser that never fires transitionend (and for
    // jsdom, which runs no transitions at all). Whichever arrives first wins;
    // `finishDismiss` makes the loser a no-op.
    clearExitTimer();
    exitTimer.current = setTimeout(finishDismiss, ms + 40);
  }, [finishDismiss, clearExitTimer, fadeBackdropOut, originRef]);

  const snapBack = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    // Spring overshoot bounce back to origin
    sheet.style.transition = 'transform 0.38s cubic-bezier(0.34, 1.56, 0.64, 1)';
    sheet.style.transform = 'translateY(0)';
  }, []);

  const handleClose = useCallback(() => {
    dismiss();
  }, [dismiss]);

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const HANDLE_ZONE = 60;
    let scrollContainer: HTMLElement | null = null;

    const onTouchStart = (e: TouchEvent) => {
      /**
       * 🚨 A new touch is always a new gesture, so the drag latch starts OFF.
       *
       * `dragging` used to be cleared in `onTouchEnd` and nowhere else, and
       * `touchcancel` was not listened for at all — so any gesture iOS took off
       * us mid-drag (a system edge swipe, a long-press menu, the WebView
       * claiming the scroll) left the latch stuck ON for the life of the sheet.
       * After that every `touchmove` skipped the drag-vs-scroll check and went
       * straight to `e.preventDefault()`, which is a NON-PASSIVE listener on
       * the sheet cancelling the body's own scroll: the sheet simply stopped
       * scrolling, with a pinned footer full of buttons below the fold and no
       * way to reach them (TKT-1290, Xero mapping — "the page appears to not
       * scroll, leaving the save button inaccessible").
       *
       * It needs a real finger: a trackpad in device emulation sends wheel
       * events and fires `touchcancel` essentially never, which is why it did
       * not reproduce in a desktop browser's mobile view.
       *
       * `touchcancel` is now bound alongside `touchend` (DocumentActionSheet and
       * PullToRefresh already did this), and this line is the belt to that
       * braces — anything that ends a gesture without either event still
       * cannot poison the next one.
       *
       * Gated on this being the FIRST finger down: a second finger landing
       * mid-drag also fires touchstart, and clearing the latch there would
       * strand the sheet mid-transform with no snap-back.
       */
      if (e.touches.length === 1) dragging.current = false;

      // Don't start drag if entry animation hasn't finished
      if (!entryDone.current) return;

      /**
       * A nested surface that owns its own vertical gesture opts out here.
       *
       * Without this the two fight and the outer sheet always wins, because
       * `scrolledToTop` is true the moment a document modal opens — so ANY
       * downward swipe, anywhere in the sheet, drags the whole modal. The
       * phone's document action sheet reads as welded to the paper behind it
       * ("if i hold and swipe the whole thing moves together as one"), which is
       * exactly what it looked like. Marking the inner surface is better than
       * having it stopPropagation: React delegates its listeners at the root,
       * so a React onTouchStart on the child runs AFTER this native one on the
       * parent and could never have stopped it.
       */
      if ((e.target as HTMLElement | null)?.closest?.('[data-sheet-nodrag]')) return;

      const touch = e.touches[0];
      const rect = sheet.getBoundingClientRect();
      const touchYRelative = touch.clientY - rect.top;

      if (!scrollContainer) {
        scrollContainer = scrollRef?.current ?? sheet.querySelector('.job-modal-form, .quote-modal-form, form');
      }

      const inHandleZone = touchYRelative <= HANDLE_ZONE;
      const scrolledToTop = !scrollContainer || scrollContainer.scrollTop <= 0;

      if (!inHandleZone && !scrolledToTop) return;

      // Kill any leftover transition from snap-back so drag feels instant
      sheet.style.transition = 'none';

      startY.current = touch.clientY;
      currentDelta.current = 0;
      positions.current = [{ y: touch.clientY, t: Date.now() }];
    };

    const onTouchMove = (e: TouchEvent) => {
      if (positions.current.length === 0) return; // touchstart was rejected

      const touch = e.touches[0];
      const delta = touch.clientY - startY.current;

      if (!dragging.current) {
        if (delta <= 3) {
          startY.current = touch.clientY;
          return;
        }

        if (!scrollContainer) {
          scrollContainer = scrollRef?.current ?? sheet.querySelector('.job-modal-form, .quote-modal-form, form');
        }
        const rect = sheet.getBoundingClientRect();
        const touchYRelative = positions.current[0]?.y ? positions.current[0].y - rect.top : 0;
        const inHandleZone = touchYRelative <= HANDLE_ZONE;
        const scrolledToTop = !scrollContainer || scrollContainer.scrollTop <= 0;

        if (!inHandleZone && !scrolledToTop) return;

        dragging.current = true;
      }

      e.preventDefault();

      // Rubber-band when dragging up past open position
      let adjustedDelta = delta;
      if (delta < 0) {
        adjustedDelta = delta * 0.12;
      }

      currentDelta.current = adjustedDelta;

      // Direct DOM manipulation — no transition, 60fps
      sheet.style.transform = `translateY(${adjustedDelta}px)`;

      // Track positions for velocity
      positions.current.push({ y: touch.clientY, t: Date.now() });
      if (positions.current.length > 5) {
        positions.current.shift();
      }
    };

    const onTouchEnd = () => {
      if (!dragging.current) {
        positions.current = [];
        return;
      }
      dragging.current = false;

      // Calculate velocity from recent positions
      const pts = positions.current;
      let velocity = 0;

      if (pts.length >= 2) {
        // Use last 2 points for more responsive velocity
        const last = pts[pts.length - 1];
        const prev = pts[pts.length - 2];
        const dt = last.t - prev.t;
        if (dt > 0) {
          velocity = (last.y - prev.y) / dt; // px/ms, positive = downward
        }
      }

      const sheetHeight = sheet.offsetHeight || window.innerHeight * 0.9;
      const dragRatio = currentDelta.current / sheetHeight;

      const earnedDismiss = velocity > 0.4 || dragRatio > 0.3;

      /**
       * 🚨 The swipe has to ask, exactly as the backdrop and Esc do.
       *
       * This branch used to call `onDismiss` itself, which is the caller's
       * *unconditional* close — so a phone swipe walked straight past the
       * unsaved-work guard that every other dismiss route goes through, and a
       * half-typed quote went with it.
       *
       * Blocked means spring back, not freeze: the sheet returns to open and
       * stays exactly as it was, with whatever the caller raised on top of it.
       */
      if (earnedDismiss && beforeDismissRef.current?.() === false) {
        snapBack();
        positions.current = [];
        return;
      }

      // Fast flick (>0.4 px/ms) or dragged past 30% → dismiss
      if (earnedDismiss) {
        // Calculate dynamic duration based on remaining distance and velocity.
        // Floor at 0.2s (not 0.12s): a harder floor keeps the slide-off
        // PERCEPTIBLE on fast flicks — below ~0.2s a full-height travel reads as
        // an instant blink, so you only register the backdrop fade and the sheet
        // seems to vanish rather than slide off the bottom.
        const remaining = sheetHeight - currentDelta.current;
        const speed = Math.max(velocity, 0.5); // minimum speed
        const duration = Math.min(0.4, Math.max(0.2, remaining / (speed * 1000)));

        // Fade the backdrop out over the same window so it doesn't snap once
        // the flicked sheet clears the screen.
        fadeBackdropOut(duration * 1000);
        setMoving(sheet, isGlassDocument());
        // Glass carries the finger's speed on and eases out at the bottom;
        // A's ease-in keeps accelerating off the screen.
        sheet.style.transition = `transform ${duration}s ${isGlassDocument() ? 'cubic-bezier(0.2, 0.6, 0.35, 1)' : 'cubic-bezier(0.4, 0, 1, 1)'}`;
        sheet.style.transform = 'translateY(100%)';

        dismissed.current = true;
        const onEnd = () => {
          sheet.removeEventListener('transitionend', onEnd);
          finishDismiss();
        };
        sheet.addEventListener('transitionend', onEnd);
        clearExitTimer();
        exitTimer.current = setTimeout(finishDismiss, duration * 1000 + 50);
      } else {
        snapBack();
      }

      positions.current = [];
    };

    /**
     * The gesture was taken off us — a system edge swipe, a long-press menu, an
     * incoming-call banner, the WebView claiming the scroll. iOS fires this
     * instead of `touchend`, so without it the drag latch and the recorded
     * positions both survive into the NEXT touch (see the note in onTouchStart
     * for what that costs).
     *
     * 🚨 Snap back, never dismiss. `onTouchEnd` reads velocity and can decide
     * the swipe earned a dismiss; a cancelled gesture is not a swipe the user
     * finished, and closing a half-filled sheet because iOS interrupted them is
     * the worse of the two failures.
     */
    const onTouchCancel = () => {
      const wasDragging = dragging.current;
      dragging.current = false;
      positions.current = [];
      currentDelta.current = 0;
      if (wasDragging) snapBack();
    };

    sheet.addEventListener('touchstart', onTouchStart, { passive: true });
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    sheet.addEventListener('touchend', onTouchEnd, { passive: true });
    sheet.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      sheet.removeEventListener('touchstart', onTouchStart);
      sheet.removeEventListener('touchmove', onTouchMove);
      sheet.removeEventListener('touchend', onTouchEnd);
      sheet.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [dismiss, snapBack, finishDismiss, clearExitTimer, fadeBackdropOut]);

  // Set will-change on mount for GPU compositing
  useEffect(() => {
    const sheet = sheetRef.current;
    if (sheet) {
      sheet.style.willChange = 'transform';
    }
  }, []);

  // Track the iOS keyboard via visualViewport — exposed as a --kb-inset CSS
  // variable on the sheet so .mucka-mod-sheet and .mucka-mod-formactions
  // can shrink to keep the focused input + sticky footer above the keyboard.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const sheet = sheetRef.current;
      const backdrop = backdropRef.current;
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // Set on both so (a) the sheet's max-height can shrink for keyboard
      // and (b) the backdrop can pad-bottom to lift the sheet above the
      // keyboard — iOS with KeyboardResize.None doesn't reflow the layout
      // viewport, so the sheet would otherwise stay anchored behind the
      // keyboard even after max-height shrink.
      sheet?.style.setProperty('--kb-inset', `${inset}px`);
      backdrop?.style.setProperty('--kb-inset', `${inset}px`);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // When the user focuses an input/textarea inside the sheet, nudge it
  // into view after the keyboard animation settles. Belt-and-braces with
  // the --kb-inset shrink above.
  useEffect(() => {
    function handleFocus(e: FocusEvent) {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const sheet = sheetRef.current;
      if (!sheet || !sheet.contains(target)) return;
      if (!/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      setTimeout(() => {
        try { target.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
        catch { /* no-op */ }
      }, 250);
    }
    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, []);

  return {
    sheetRef,
    backdropRef,
    sheetStyle: {},
    backdropStyle: {},
    handleClose,
  };
}
